import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useProgram } from "@/contexts/program";
import { usePortalScope } from "@/contexts/portalScope";
import { programService, type ProgramDetails } from "@/services/program.service";
import { sneakylinkService } from "@/services/sneakylink.service";
import { affiliateDashboardApi } from "@/services/affiliateDashboard.service";
import { ScopedInvitationAlert } from "@/components/affiliate/ScopedInvitationAlert";
import { PayoutMethodBanner } from "@/components/affiliate/PayoutMethodBanner";
import { EarningsCard } from "@/components/affiliate/dashboard/earnings-card";
import { ProgramCard } from "@/components/affiliate/dashboard/program-card";
import { RecurringCard } from "@/components/affiliate/dashboard/recurring-card";
import { TikTokCard } from "@/components/affiliate/dashboard/tiktok-card";
import { SalesCard } from "@/components/affiliate/dashboard/sales-card";
import { GoalCard } from "@/components/affiliate/dashboard/goal-card";
import { PayoutsCard } from "@/components/affiliate/dashboard/payouts-card";
import { AffiliateDashboardSkeleton } from "@/components/affiliate/dashboard/DashboardSkeleton";
import { BrandSwitcher } from "@/components/affiliate/dashboard/brand-switcher";

/** Active programs, with the share link attached for link-tracked ones. */
async function loadPrograms(known: ProgramDetails[]): Promise<ProgramDetails[]> {
  const programs = known.length > 0 ? known : await programService.findActivePrograms();
  return Promise.all(
    programs.map(async (program) => {
      if (program.attributionMethod !== "SNEAKYLINK") return program;
      try {
        const links = await sneakylinkService.getMySneakylinks(program.program.id);
        return links.length > 0 ? { ...program, sneakylink: links[0] } : program;
      } catch (err) {
        console.error(`Failed to fetch sneakylink for program ${program.program.id}:`, err);
        return program;
      }
    })
  );
}

/**
 * Everything on this page is the affiliate's own: what they earned, what
 * they share, where they stand with the brand, and what is on its way to
 * them. No brand-wide totals, no other affiliates.
 */
export function AffiliateDashboard() {
  const { isMarketplaceHidden } = usePortalScope();
  const { activePrograms: knownPrograms, isLoading: programsContextLoading } = useProgram();
  const [selectedId, setSelectedId] = useState<string>("");

  const programsQuery = useQuery({
    queryKey: ["affiliate-dashboard-programs", knownPrograms.map((p) => p.id).join(",")],
    queryFn: () => loadPrograms(knownPrograms),
    enabled: !programsContextLoading,
    staleTime: 60_000,
  });
  const programs = useMemo(() => programsQuery.data ?? [], [programsQuery.data]);

  useEffect(() => {
    if (programs.length > 0 && !programs.some((p) => p.id === selectedId)) {
      setSelectedId(programs[0].id);
    }
  }, [programs, selectedId]);

  const selected = useMemo(() => programs.find((p) => p.id === selectedId), [programs, selectedId]);
  const programId = selected?.program.id;

  const hubQuery = useQuery({
    queryKey: ["affiliate-program-hub", programId],
    queryFn: () => programService.getAffiliateHub(programId!),
    enabled: !!programId,
    staleTime: 60_000,
    retry: 1,
  });
  const winsQuery = useQuery({
    queryKey: ["affiliate-recent-wins", programId ?? "all"],
    queryFn: () => affiliateDashboardApi.getRecentWins(programId),
    enabled: !!programId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const summaryQuery = useQuery({
    queryKey: ["affiliate-earnings-summary"],
    queryFn: () => affiliateDashboardApi.getEarningsSummary(),
    staleTime: 60_000,
  });
  const payoutsQuery = useQuery({
    queryKey: ["affiliate-payout-history", programId ?? "all"],
    queryFn: () => affiliateDashboardApi.getPayoutHistory({ limit: 5, programId }),
    enabled: !!programId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  // One brand in a scoped portal, already selected: a dropdown whose only
  // option is the brand you are inside cannot do anything.
  const showSelect = !isMarketplaceHidden && programs.length > 1;

  if (programsContextLoading || programsQuery.isLoading || (selected && hubQuery.isLoading && !hubQuery.data)) {
    return <AffiliateDashboardSkeleton withSelect={!isMarketplaceHidden} />;
  }

  return (
    <div className="flex-1 space-y-4">
      {/* The scoped portal has no Invitations page: at most one invitation
          exists, so it is surfaced where the affiliate lands. */}
      <ScopedInvitationAlert />
      {/* Money is waiting and there is nowhere to send it. A banner, not a
          wall; payout details are only required at the request itself. */}
      <PayoutMethodBanner />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 sm:mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Dashboard</h1>
        {showSelect && <BrandSwitcher programs={programs} value={selectedId} onChange={setSelectedId} />}
      </div>

      {programs.length === 0 ? (
        <section className="flex min-h-[16rem] flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card px-6 text-center">
          <p className="text-[15px] font-semibold text-foreground">No programs yet</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {isMarketplaceHidden
              ? "Your program shows up here once the brand adds you."
              : "Join a brand's program to get a code and start earning."}
          </p>
          {!isMarketplaceHidden && (
            <Link
              to="/affiliate/brands/search"
              className="text-sm font-medium text-brand-700 underline-offset-4 hover:underline"
            >
              Find brands
            </Link>
          )}
        </section>
      ) : (
        selected && (
          <>
            {hubQuery.data && <EarningsCard key={programId} hub={hubQuery.data} program={selected} />}

            {/* The right column sets this row's height; the sales card fills
                it and scrolls inside, whether it holds twenty rows or none. */}
            <div className="grid gap-4 lg:grid-cols-12">
              <div className="relative min-w-0 lg:col-span-8">
                <div className="lg:absolute lg:inset-0">
                  <SalesCard data={winsQuery.data} isLoading={winsQuery.isLoading} />
                </div>
              </div>
              <div className="min-w-0 space-y-4 lg:col-span-4">
                {hubQuery.data && <ProgramCard hub={hubQuery.data} />}
                {hubQuery.data && <RecurringCard hub={hubQuery.data} />}
                {programId && <TikTokCard programId={programId} />}
                <GoalCard summary={summaryQuery.data} />
                <PayoutsCard data={payoutsQuery.data?.data} isLoading={payoutsQuery.isLoading} />
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

export default AffiliateDashboard;
